/**
 * ScoreTip.tsx —— 行动后决策评分提示（第 10 轮 R3 + R4）
 *
 * 第 10 轮的两处升级：
 *   - R3（事件驱动）：提示不再倒计时自动淡出。是否可见由 controller 的 `lastScore` 与本组件的
 *     显式关闭（✕ / Esc，由父层记录 `dismissedAt`）共同决定；「常驻到下一手操作」的关闭时机
 *     全部由 controller 事件负责（提交下一动作 / 进入新一手需重新行动）。
 *   - R4（三段式失误解析）：`mistake` / `blunder` 必现三段（①你的选择 ②为什么 ③更优打法），
 *     `optimal` / `acceptable` 默认只显示徽标 + 一句话结论，点「详解」/ 徽标展开三段（AC4.9）。
 *
 * 多条评分（同一手玩家多次行动）：只常驻**最新一条** + `本手 k/n ‹ ›` 翻页（DESIGN 4.2）。
 * 无论关闭与否，`复盘` 入口都能进 `HistoryDrawer → 决策评分` 再看一次（R3.5）。
 *
 * 两种承载形态由 `variant` 决定，样式差异全在 layout.css：
 *   - `inline`：常规屏放在页脚常驻提示槽里（页脚高度恒定，不引起赢率浮层位移）；
 *   - `float` ：短屏页脚提示槽让出，改成顶部可点开的角标 chip（不 0×0 隐藏）。
 */

import { useState } from 'react';
import { buildScoreDetail, type ScoreRecord } from '../state/scoring';
import type { ScoreToneMode } from '../state/settings';
import { signedChips } from '../state/format';

export function ScoreTip({
  records,
  variant,
  tone = 'pro',
  handResult = null,
  onReplay,
  onClose,
}: {
  /** 本手已产生的评分记录（时间序）；组件默认常驻最新一条并支持 k/n 翻页。 */
  records: readonly ScoreRecord[];
  variant: 'inline' | 'float';
  /** 需求二 FR2-2：评分语气模式（新手鼓励 / 专业直述），影响评级措辞 + 配色 + 详解语气，不改数值。 */
  tone?: ScoreToneMode;
  /**
   * T8 · AC-N1：本手结果（仅净盈利 > 0 时传入）。存在时评分区**首行先陈述结果**
   * 「你赢下 +78 · 两对」，把 EV 差异等负面措辞降为次级信息（不出现在首行）。
   */
  handResult?: { net: number; handText: string | null } | null;
  onReplay: () => void;
  /** 显式关闭（✕）；Esc 关闭由父层统一监听。 */
  onClose: () => void;
}) {
  const total = records.length;
  // 翻页游标：null = 跟随最新一条；产生新评分（length 变化）时自动回到最新（R3.2 替换语义）。
  const [pageAt, setPageAt] = useState<number | null>(null);
  const [lastLen, setLastLen] = useState(total);
  if (lastLen !== total) {
    setLastLen(total);
    setPageAt(null);
  }
  // T8 · AC-N1：评分卡**默认折叠**——详解仅在用户主动展开后显示（含 mistake / blunder，不再强制展开）。
  const [userExpanded, setUserExpanded] = useState(false);

  if (total === 0) return null;
  const latest = total - 1;
  const idx = pageAt === null ? latest : Math.min(Math.max(pageAt, 0), latest);
  const rec = records[idx];
  const detail = buildScoreDetail(rec, tone);
  const tierName = detail.tierName;
  const showDetail = userExpanded;
  // T8 · AC-N1：赢牌手先陈述结果（正则可断言 `^你赢下.*[+＋]\d`），负面措辞降为次级、不进首行。
  const wonLine =
    handResult && handResult.net > 0
      ? `你赢下 ${signedChips(handResult.net)}${handResult.handText ? ` · ${handResult.handText}` : ''}`
      : null;

  return (
    <div
      className={`scoretip scoretip--${variant} tier--${rec.tier} scoretip--tone-${detail.toneClass}${showDetail ? ' scoretip--open' : ''}${wonLine ? ' scoretip--won' : ''}`}
      role="status"
    >
      {wonLine ? <p className="scoretip__result">{wonLine}</p> : null}
      <div className="scoretip__bar">
        <button
          type="button"
          className="scoretip__badge"
          aria-expanded={showDetail}
          aria-label={`决策评分：${tierName}。${detail.oneLine}。点击${showDetail ? '收起' : '展开'}详解`}
          onClick={() => setUserExpanded((v) => !v)}
        >
          <b>{tierName}</b>
        </button>
        <span className="scoretip__oneline">{detail.oneLine}</span>

        {total > 1 ? (
          <span className="scoretip__pager" role="group" aria-label="本手评分翻页">
            <button
              type="button"
              className="scoretip__pg"
              aria-label="上一条评分"
              disabled={idx === 0}
              onClick={() => setPageAt(Math.max(0, idx - 1))}
            >
              ‹
            </button>
            <span className="scoretip__count num">
              本手 {idx + 1}/{total}
            </span>
            <button
              type="button"
              className="scoretip__pg"
              aria-label="下一条评分"
              disabled={idx === latest}
              onClick={() => setPageAt(Math.min(latest, idx + 1))}
            >
              ›
            </button>
          </span>
        ) : null}

        {/* T8 · AC-N1：评分卡默认折叠——所有档位都靠此键展开/收起详解（mistake/blunder 不再强制展开）。 */}
        <button type="button" className="scoretip__more" aria-expanded={showDetail} onClick={() => setUserExpanded((v) => !v)}>
          {showDetail ? '收起' : '详解'}
        </button>
        <button type="button" className="scoretip__replay" onClick={onReplay}>
          复盘
        </button>
        <button type="button" className="scoretip__close" aria-label="关闭本条评分提示" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* 详解（R4 + 需求二）：T8 起一律默认折叠、由用户展开。段落已按模板 + 语气产出，
          `边际 / 复杂`模板只给客观事实 + 中性提示（无方向性建议），`确定性错误`模板含「更优打法」。 */}
      {showDetail ? (
        <div className="scoretip__detail" role="group" aria-label="决策评分详解">
          <p className="scoretip__seg">{detail.actionLine}</p>
          {detail.segs.map((seg, i) => (
            <p
              key={i}
              className={`scoretip__seg${seg.startsWith('更优打法') ? ' scoretip__seg--best' : ''}${seg.startsWith('ⓘ') ? ' scoretip__seg--note' : ''}`}
            >
              {seg}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
